import ScriptsForm from "@/components/forms/ScriptsForm";
import { DEFAULT_HEAD_SCRIPTS } from "@/config/scripts";

import { ScriptsFormSchemaInput } from "@/utilities/validators/scripts-form";
import cn from "@/utils/class-names";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { BsQuestionCircle } from "react-icons/bs";
import { Button, Input, Modal, Switch, Title, Tooltip } from "rizzui";

export interface ScriptsFormModalProps {
  initialValues?: ScriptsFormSchemaInput;
  onSave?: (data: ScriptsFormSchemaInput) => void;
  onClose?: () => void;
  open?: boolean;
  useProjectSgTrackingId?: boolean;
}

export default function ScriptsFormModal({
  initialValues,
  onSave,
  onClose,
  open = false,
  useProjectSgTrackingId,
}: ScriptsFormModalProps) {
  // NOTE: If the project has a sg tracking id, then the default value for the useEnquiryScript is true
  const form = useForm<ScriptsFormSchemaInput>({
    defaultValues: {
      conversionId: initialValues?.conversionId,
      conversionLabel: initialValues?.conversionLabel,
      pixelID: initialValues?.pixelID,
      headerScript: useProjectSgTrackingId
        ? DEFAULT_HEAD_SCRIPTS
        : initialValues?.headerScript || "",
      bodyScript: initialValues?.bodyScript || "",
      useEnquiryScript: useProjectSgTrackingId
        ? true
        : !!initialValues?.useEnquiryScript,
    },
  });

  const onSubmit = (data: ScriptsFormSchemaInput) => {
    onSave?.(data);
  };

  return (
    <Modal size="lg" isOpen={open} onClose={onClose || (() => {})}>
      <div className="flex h-full flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2 items-center">
            <Title className="mb-2" as="h3">
              Page Scripts
            </Title>
            {useProjectSgTrackingId && (
              <Tooltip
                content={
                  <div className="flex items-start flex-col gap-1 max-w-[320px]">
                    <p className="text-sm text-left mb-1 font-bold">
                      Why you cannot setup scripts?
                    </p>
                    <p className="text-xs text-left">
                      Admin has been using ProjectSG Tracking ID for this
                      landing page.
                    </p>
                    <p className="text-xs text-left">
                      Please contact Admin to support.
                    </p>
                  </div>
                }
              >
                <button
                  type="button"
                  className="flex size-5 items-center justify-center "
                >
                  <BsQuestionCircle className="size-4" />
                </button>
              </Tooltip>
            )}
          </div>
          <p className="text-sm text-gray-500">
            Setup the scripts for the landing page
          </p>
        </div>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid gap-6 grid-cols-3"
          >
            <Controller
              control={form.control}
              name="useEnquiryScript"
              render={({ field }) => (
                <Switch
                  label="Default Thank You page"
                  checked={!!field.value}
                  onChange={(value) => field.onChange(value)}
                  labelPlacement="left"
                  className={cn(
                    "flex justify-end col-span-full",
                    useProjectSgTrackingId &&
                      "cursor-not-allowed opacity-50 pointer-events-none"
                  )}
                  readOnly={useProjectSgTrackingId}
                  // disabled={useProjectSgTrackingId}
                />
              )}
            />
            <Controller
              control={form.control}
              name="conversionId"
              // disabled={useProjectSgTrackingId}
              render={({ field }) => (
                <Input
                  labelClassName="rizzui-textarea-label block text-sm mb-1.5 font-medium"
                  label="Conversion ID"
                  type="text"
                  size="sm"
                  placeholder="Enter conversion ID"
                  autoComplete="off"
                  className="[&>label>span]:font-medium col-span-1"
                  {...field}
                />
              )}
            />
            <Controller
              control={form.control}
              name="conversionLabel"
              // disabled={useProjectSgTrackingId}
              render={({ field }) => (
                <Input
                  labelClassName="rizzui-textarea-label block text-sm mb-1.5 font-medium"
                  label="Label ID"
                  type="text"
                  size="sm"
                  placeholder="Enter label ID"
                  autoComplete="off"
                  className="[&>label>span]:font-medium col-span-1"
                  {...field}
                />
              )}
            />
            <Controller
              control={form.control}
              name="pixelID"
              render={({ field }) => (
                <Input
                  labelClassName="rizzui-textarea-label block text-sm mb-1.5 font-medium"
                  label="Pixel ID"
                  type="text"
                  size="sm"
                  placeholder="Enter pixel ID"
                  autoComplete="off"
                  className="[&>label>span]:font-medium col-span-1"
                  {...field}
                />
              )}
            />
            <ScriptsForm
              className="col-span-full"
              visible={{
                headerScript: true,
                bodyScript: true,
              }}
              props={{
                headerScript: {
                  disabled: useProjectSgTrackingId,
                },
                bodyScript: {
                  disabled: useProjectSgTrackingId,
                },
              }}
            />
            <div className="flex flex-row gap-2 justify-end mt-4 col-span-full">
              {onClose && (
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              )}
              <Button
                // disabled={useProjectSgTrackingId}
                type="submit"
              >
                Save
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
}
